import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/ui/Layout';
import LoginForm from './components/auth/LoginForm';
import AcceptInvite from './pages/AcceptInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Apply from './pages/Apply';
import PlayerApplications from './pages/PlayerApplications';
import Registrations from './pages/Registrations';
import EmailLog from './pages/EmailLog';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import PlayersList from './components/players/PlayersList';
import Calendar from './components/calendar/Calendar';
import Events from './pages/Events';
import Messages from './pages/Messages';
import Board from './pages/Board';
import Bookings from './pages/Bookings';
import Venues from './pages/Venues';
import Documents from './pages/Documents';
import AgeGroups from './pages/AgeGroups';
import Profile from './pages/Profile';
import Matches from './pages/Matches';
import Statistics from './pages/Statistics';
import TrainingStatistics from './pages/TrainingStatistics';
import Drills from './pages/Drills';
import Travel from './pages/Travel';
import Schools from './pages/Schools';
import IFA from './pages/IFA';
import MembershipFees from './pages/MembershipFees';
import ClubCustomization from './pages/ClubCustomization';
import SystemSettings from './pages/SystemSettings';
import AiAssistant from './pages/AiAssistant';
import VenueOccupancy from './pages/VenueOccupancy';
import Help from './pages/Help';
import Features from './pages/Features';
import FamilyApp from './pages/family/FamilyApp';
import { isFamilyUser } from './types/auth';
import { ClubSettingsProvider } from './contexts/ClubSettingsContext';
import { ToastProvider } from './contexts/ToastContext';
import { GlobalModalProvider } from './contexts/GlobalModalContext';
import { PushNotificationProvider } from './contexts/PushNotificationContext';
import { LearningModeProvider } from './contexts/LearningModeContext';
import MessageNotification from './components/messages/MessageNotification';
import PushPermissionPrompt from './components/notifications/PushPermissionPrompt';
import { signalRService } from './services/signalr';
import { Message } from './types/message';
import './App.css';

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState<Message[]>([]);

  // Az aktuális útvonal ref-ben, hogy a SignalR kapcsolatnak ne kelljen minden
  // navigációnál újraépülnie (korábban oldalváltásonként bontott és csatlakozott)
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    signalRService.connect(token).catch((error) => {
      console.error('Failed to connect to SignalR:', error);
    });

    const unsubscribe = signalRService.onMessage((message) => {
      // Az üzenetek oldalon nem kell külön értesítés
      if (!pathnameRef.current.startsWith('/messages')) {
        setNotifications((prev) => [...prev, message]);
      }
    });

    return () => {
      unsubscribe();
      signalRService.disconnect();
    };
  }, [userId]);

  const handleCloseNotification = (messageId: number) => {
    setNotifications((prev) => prev.filter((m) => m.id !== messageId));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginForm />} />
        <Route path="/register" element={<Register />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        {/* A bejelentkezés utáni célt állapotban visszük, nem query paraméterben:
            a címsorban maradjon egyszerűen /login. */}
        <Route
          path="*"
          element={<Navigate to="/login" state={{ from: location.pathname + location.search }} replace />}
        />
      </Routes>
    );
  }

  // A nyilvános jelentkezési űrlap bejelentkezve is a saját, menü nélküli formájában
  // jelenjen meg - pl. ha az edző ellenőrzi, mit lát a szülő.
  if (location.pathname === '/apply') {
    return <Apply />;
  }

  return (
    <>
      {/* A szülők és a játékosok külön, mobilappszerű felületet kapnak */}
      {isFamilyUser(user) ? (
        <FamilyApp />
      ) : (
        <Layout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route
              path="/applications"
              element={
                <ProtectedRoute adminOnly>
                  <PlayerApplications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute adminOnly>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/age-groups"
              element={
                <ProtectedRoute adminOnly>
                  <AgeGroups />
                </ProtectedRoute>
              }
            />
            <Route
              path="/players"
              element={
                <ProtectedRoute>
                  <PlayersList />
                </ProtectedRoute>
              }
            />
            {/* Keresésből érkező mélylink egy játékosra */}
            <Route
              path="/players/:id"
              element={
                <ProtectedRoute>
                  <PlayersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <Events />
                </ProtectedRoute>
              }
            />
            {/* Értesítésből és keresésből érkező mélylink egy eseményre */}
            <Route
              path="/events/:id"
              element={
                <ProtectedRoute>
                  <Events />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/board"
              element={
                <ProtectedRoute>
                  <Board />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <Bookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/board/:id"
              element={
                <ProtectedRoute>
                  <Board />
                </ProtectedRoute>
              }
            />
            <Route
              path="/venues"
              element={
                <ProtectedRoute adminOnly>
                  <Venues />
                </ProtectedRoute>
              }
            />
            <Route
              path="/venue-occupancy"
              element={
                <ProtectedRoute>
                  <VenueOccupancy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <Documents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matches"
              element={
                <ProtectedRoute>
                  <Matches />
                </ProtectedRoute>
              }
            />
            <Route
              path="/drills"
              element={
                <ProtectedRoute>
                  <Drills />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai"
              element={
                <ProtectedRoute>
                  <AiAssistant />
                </ProtectedRoute>
              }
            />
            <Route
              path="/schools"
              element={
                <ProtectedRoute adminOnly>
                  <Schools />
                </ProtectedRoute>
              }
            />
            <Route
              path="/travel"
              element={
                <ProtectedRoute>
                  <Travel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/training-statistics"
              element={
                <ProtectedRoute>
                  <TrainingStatistics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistics"
              element={
                <ProtectedRoute>
                  <Statistics />
                </ProtectedRoute>
              }
            />
            {/* A régi, külön Edzések oldal helyett az Események kezel mindent */}
            <Route path="/trainings" element={<Navigate to="/events" replace />} />
            <Route
              path="/ifa"
              element={
                <ProtectedRoute adminOnly>
                  <IFA />
                </ProtectedRoute>
              }
            />
            <Route
              path="/membership-fees"
              element={
                <ProtectedRoute>
                  <MembershipFees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/club-settings"
              element={
                <ProtectedRoute adminOnly>
                  <ClubCustomization />
                </ProtectedRoute>
              }
            />
            <Route
              path="/system-settings"
              element={
                <ProtectedRoute adminOnly>
                  <SystemSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/registrations"
              element={
                <ProtectedRoute adminOnly>
                  <Registrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-log"
              element={
                <ProtectedRoute adminOnly>
                  <EmailLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/features"
              element={
                <ProtectedRoute>
                  <Features />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      )}

      {/* Értesítések bekapcsolására biztató sáv (csak amíg nincs bekapcsolva) */}
      <PushPermissionPrompt />

      {/* Message Notifications */}
      <div className="fixed bottom-0 left-0 z-50 space-y-2 p-4">
        {notifications.map((notification) => (
          <MessageNotification
            key={notification.id}
            message={notification}
            onClose={() => handleCloseNotification(notification.id)}
          />
        ))}
      </div>
    </>
  );
};

function App() {
  return (
    <ClubSettingsProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <GlobalModalProvider>
              <PushNotificationProvider>
                <LearningModeProvider>
                  <div className="App">
                    <AppRoutes />
                  </div>
                </LearningModeProvider>
              </PushNotificationProvider>
            </GlobalModalProvider>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ClubSettingsProvider>
  );
}

export default App;